import React, { useState, useEffect } from 'react';
import { Layout, Collapse, Checkbox, Slider, Card, Row, Col, Button, Drawer, Image, Typography, Select } from 'antd';
import { MenuOutlined, AppstoreOutlined, UnorderedListOutlined, StarOutlined, SwapOutlined, EyeOutlined, ShoppingCartOutlined, StarFilled, ShopOutlined } from '@ant-design/icons';
import DynamicBreadcrumb from 'components/DynamicBreadcrumb';
import { Link } from 'react-router-dom';
import OurServices from 'components/frontend/OurServices';
import { useProductsContext } from 'contexts/ProductsContext';
import { useCartContext } from 'contexts/CartContext';
import { useWishlistContext } from 'contexts/WishlistContext';

const { Sider, Content } = Layout;
const { Text } = Typography;

const ProductPage = () => {
    const { products, isProductsLoading } = useProductsContext();
    const { addToCart, cartItems } = useCartContext();
    const { addToWishlist, wishlistItems } = useWishlistContext();
    const [visible, setVisible] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [filters, setFilters] = useState({
        categories: [],
        colors: [],
        sizes: []
    });

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, priceRange, products]);

    const applyFilters = () => {
        if (!products.length) return;

        const result = products.filter(product => {
            // Ensure product attributes are defined before filtering
            const productCategory = product.category?.toLowerCase() || "";
            const productColors = product.colors?.map(color => color.toLowerCase()) || [];
            const productSizes = product.sizes?.map(size => size.toLowerCase()) || [];

            // Category filter
            const categoryMatch = filters.categories.length === 0 ||
                filters.categories.some(cat => productCategory.includes(cat.toLowerCase()));

            // Price filter
            const priceMatch = product.price >= priceRange[0] && product.price <= priceRange[1];

            // Color filter
            const colorMatch = filters.colors.length === 0 ||
                filters.colors.some(color => productColors.includes(color.toLowerCase()));

            // Size filter
            const sizeMatch = filters.sizes.length === 0 ||
                filters.sizes.some(size => productSizes.includes(size.toLowerCase()));

            // All filters must match for the product to be included
            return categoryMatch && priceMatch && colorMatch && sizeMatch;
        });

        setFilteredProducts(result);
    };


    const showDrawer = () => {
        setVisible(true);
    };

    const onClose = () => {
        setVisible(false);
    };

    const toggleViewMode = () => {
        setViewMode(viewMode === 'grid' ? 'list' : 'grid');
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterType]: Array.isArray(value) ? value : [value]
        }));
    };

    const CustomCheckboxGroup = ({ options, filterType }) => (
        <div className='d-flex flex-column'>
            {options.map(option => (
                <Checkbox
                    key={option.value}
                    onChange={(e) => {
                        const newValue = e.target.checked
                            ? [...filters[filterType], option.value]
                            : filters[filterType].filter(v => v !== option.value);
                        handleFilterChange(filterType, newValue);
                    }}
                    checked={filters[filterType].includes(option.value)}
                >
                    {option.label}
                </Checkbox>
            ))}
        </div>
    );
    // console.log('filteredProducts', filteredProducts)


    const items = [
        {
            key: '1',
            label: 'Product Brand',
            children: (
                <CustomCheckboxGroup
                    options={[
                        { label: 'Error aliqua Autem', value: 'Error aliqua Autem' },
                        // Add more brands as needed
                    ]}
                    filterType="categories"
                />
            ),
        },
        {
            key: '2',
            label: 'Filter by Price',
            children: (
                <Slider
                    range
                    min={0}
                    max={1000}
                    value={priceRange}
                    onChange={(value) => setPriceRange(value)}
                />
            ),
        },
        {
            key: '3',
            label: 'Filter by Color',
            children: (
                <CustomCheckboxGroup
                    options={[
                        { label: 'Purple', value: 'purple' },
                        { label: 'Blue', value: 'blue' },
                    ]}
                    filterType="colors"
                />
            ),
        },
        {
            key: '4',
            label: 'Filter by Size',
            children: (
                <CustomCheckboxGroup
                    options={[
                        { label: 'M', value: 'm' },
                        { label: 'XL', value: 'xl' },
                        { label: 'XXL', value: 'xxl' },
                    ]}
                    filterType="sizes"
                />
            ),
        },
    ];

    const SidebarContent = () => (
        <Collapse defaultActiveKey={['1', '2', '3', '4']} ghost expandIconPosition="end" items={items} />
    );

    const handleAddToCart = async (product) => {
        await addToCart(product);
        window.toastify(`${product.name} added to cart`, "success");
    };

    const handleAddToWishlist = async (product) => {
        await addToWishlist(product);
        window.toastify(`${product.name} added to wishlist`, "success");
    };

    const isInWishlist = (product) => wishlistItems.some(item => item.id === product.id);
    const isInCart = (product) => cartItems.some(item => item.id === product.id);


    return (
        <div className="container my-5 overflow-hidden">
            <DynamicBreadcrumb />
            <Layout style={{ background: 'transparent' }}>
                <Sider width={250} style={{ background: 'transparent' }} className="d-none d-md-block">
                    <SidebarContent />
                </Sider>
                <Layout style={{ marginLeft: 0, background: 'transparent' }}>
                    <Content className="px-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <Button type="primary" onClick={showDrawer} className="d-md-none">
                                <MenuOutlined />
                            </Button>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <Button type="text" icon={viewMode === 'grid' ? <UnorderedListOutlined /> : <AppstoreOutlined />} onClick={toggleViewMode} className="ml-3" />
                                <div className='d-flex align-items-center gap-3'>
                                    <span className="d-none d-md-inline ms-auto">Showing 1–{filteredProducts.length} of {filteredProducts.length} results</span>
                                    <Select defaultValue="newest" style={{ width: 120 }}
                                        options={[
                                            { value: 'newest', label: 'Newest' },
                                            { value: 'priceHighToLow', label: 'Price: High to Low' },
                                            { value: 'priceLowToHigh', label: 'Price: Low to High' },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                        <Row gutter={[16, 16]}>
                            {isProductsLoading ? (
                                <Col span={24}>
                                    <div>Loading products...</div>
                                </Col>
                            ) : (
                                filteredProducts.map((product) => (
                                    <Col xs={24} sm={24} md={24} lg={viewMode === 'grid' ? 8 : 24} key={product.id + 1}>
                                        <Card className={`p-0 product-card bg-transparent ${viewMode === 'list' ? 'list-view' : ''} p-0 m-0 border-0`} styles={{ body: { padding: "0" } }}>
                                            <Row>
                                                <Col xs={24} md={viewMode === 'list' ? 12 : 24}>
                                                    <div className="product-image-container">
                                                        <Image src={product.images[0]} alt={product.name} preview={false} width="100%" height={viewMode === 'list' ? 250 : 350} />
                                                        <div className="product-overlay">
                                                            <div className="overlay-icons">
                                                                <Button icon={isInWishlist(product) ? <StarFilled /> : <StarOutlined />} shape="circle" disabled={isInWishlist} onClick={() => handleAddToWishlist(product)} />
                                                                <Button icon={<SwapOutlined />} shape="circle" />
                                                                <Link to={`/product?category=${encodeURIComponent(product.slug)}`} className='text-decoration-none'>
                                                                    <Button icon={<EyeOutlined />} shape="circle" />
                                                                </Link>
                                                            </div>
                                                            <Button
                                                                type="primary"
                                                                className="bg-white text-primary fw-medium add-to-cart-btn mt-3"
                                                                onClick={() => handleAddToCart(product)}
                                                                disabled={isInCart(product)}
                                                                icon={isInCart(product) ? <ShopOutlined /> : <ShoppingCartOutlined />}
                                                            >
                                                                {isInCart(product) ? 'In Cart' : 'Add To Cart'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col xs={24} md={viewMode === 'list' ? 12 : 24}>
                                                    <div className="product-content p-3">
                                                        <Link to={`/product?category=${encodeURIComponent(product.slug)}`} className='text-decoration-none'>
                                                            <Text strong className='fw-bold fs-5'>{product.name}</Text>
                                                        </Link>
                                                        <br />
                                                        <Text strong>{product.description}</Text>
                                                        <div className="pricing mt-2">
                                                            <Text delete className="fs-6">${product.price.toFixed(2)}</Text>
                                                            <Text strong className="text-primary ml-2 ms-3 fs-5">${product.originalPrice.toFixed(2)}</Text>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                ))
                            )}
                        </Row>
                    </Content>
                </Layout>
            </Layout>
            <OurServices />
            <Drawer title="Filters" placement="right" onClose={onClose} open={visible}>
                <SidebarContent />
            </Drawer>
        </div>
    );
};

export default ProductPage;